<script setup lang="ts">
import { bemBuilder, toMoney } from "@chatfood/core-utils";
import { AtomIcon, AtomText } from "@chatfood/design-system";
import { computed, ref } from "vue";
import { PriceBreakdown } from "../price-breakdown";
import type { IPriceBreakdownToggleProps } from "./props";
import { useBasketStore } from "~/stores/basket";
import { useLocale } from "~/composables/use-locale";
import { usePriceBreakdownProps } from "~/composables/use-price-breakdown-props";

const css = bemBuilder("price-breakdown-toggle");
const { t } = useLocale();

const props = defineProps<IPriceBreakdownToggleProps>();

const basket = useBasketStore();
const breakdownProps = usePriceBreakdownProps();

const isExpanded = ref(false);

const displayTotal = computed(() => {
  const base = basket.totalBreakdown.outstandingBalance;
  if (!props.tipAmount) return base;
  return {
    value: base.value + props.tipAmount.value,
    currency: base.currency,
  };
});

function toggle() {
  isExpanded.value = !isExpanded.value;
}
</script>

<template>
  <div v-if="basket.items.length > 0" :class="css('')">
    <Transition name="accordion">
      <div v-if="isExpanded" :class="css('detail')">
        <PriceBreakdown
          v-bind="breakdownProps"
          :tip-amount="props.tipAmount"
          hide-final-total
        />
      </div>
    </Transition>
    <button
      :class="css('button')"
      :aria-label="t('components.price_breakdown_toggle.label')"
      :aria-expanded="isExpanded"
      @click="toggle"
    >
      <AtomText type="body-bold" color="color-text-body">
        {{ t("components.price_breakdown.charges_total") }}
      </AtomText>
      <span :class="css('right')">
        <AtomText type="body-bold" color="color-text-body">
          {{ toMoney(displayTotal.value, displayTotal.currency) }}
        </AtomText>
        <AtomIcon
          name="caret-right"
          size="small"
          ignore-color
          :class="[css('chevron'), { [css('chevron', 'open')]: isExpanded }]"
        />
      </span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
$accordion-max-height: min(40vh, 360px);

.price-breakdown-toggle {
  &__button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font: inherit;
    color: inherit;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
  }

  &__chevron {
    rotate: 90deg;
    transition: rotate 250ms ease-in-out;

    &--open {
      rotate: -90deg;
    }
  }

  &__detail {
    max-height: $accordion-max-height;
    overflow-y: auto;
    padding-bottom: var(--spacing-sm);
    margin-bottom: var(--spacing-sm);
    border-bottom: 1px solid var(--color-base-95);
  }
}

.accordion-enter-active,
.accordion-leave-active {
  transition: max-height 0.3s ease-in-out;
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
}

.accordion-enter-to,
.accordion-leave-from {
  max-height: $accordion-max-height;
}
</style>
